import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import { CommonProperties, CommonStyles, Color } from '../../styles';
import Icon from '../Icon';
import RowView from '../RowView';
import BaseText from '../BaseText';
import TitleSummaryList from '../tripSummary/TitleSummary';
import InvoiceSummaryList from './InvoiceSummaryList';
import LoaderOverlay from '../modals/LoaderOverlay';
import { useComponentTKey, i18nNS } from '../../i18n';
import { useTranslation } from 'react-i18next';
import CircleSegmentedControl from '../SegmentedControl/CircleSegmentedControl';
import {
  selectReceiptIsCancelable,
  selectReceiptIsChangeable,
  selectFirstTripSelectedAir,
  selectFirstReceipt,
  selectReceiptMultiPnr,
  hasNonSabreHotel,
} from '../../store/selectors/mytrips.selector';
import useTripOptions from '../../utils/useTripOptions';
import useAvailableHeight from '../../utils/useAvailableHeight';
import {
  getLoyaltyAir,
  getLoyaltyCar,
  getLoyaltyHotel,
  getLoyaltyRail,
} from '../../services/api/profile.service';
import Images from '../../constants/Images';
import { REBOOK_ENABLED } from '../../env.rebook';
import InvoiceSummaryForMultiPnr from './InvoiceSummaryForMultiPnr';
import { selectUserProfile } from '../../store/selectors/profile.selector';
import { AA_ENABLED, EVENTS_ENABLED } from '../../config/env';
import { pageKeys } from '../../constants/PageKeys';
import Messages from '../event/Messages';
import { selectTezEvent, selectTezEventGroup } from '../../store/selectors/events.selector';
import {
  selectCompanyCurrency,
  selectCompanyCurrencyCulture,
  selectTokenData,
} from '../../store/selectors/auth.selector';
import { formatCurrency } from '../../utils/currency.utils';
const getAirIcon = (code) => {
  return code ? Images.air['36x36'][code] : null;
};

const getHotelIcon = (code) => {
  return code ? Images.hotel['24x24'][code] : null;
};

const getCarIcon = (code) => {
  return code ? Images.car['50x20'][code] : null;
};

const getRailIcon = (code) => {
  return code ? Images.rail['24x24'][code] : null;
};
const InvoiceSummary = () => {
  const receiptResponseIsChangeable = useSelector(selectReceiptIsChangeable);
  const receiptResponseIsCancelable = useSelector(selectReceiptIsCancelable);
  const hasNonSabreHotelBool = useSelector(hasNonSabreHotel);
  const companySupportDetails = useSelector(selectUserProfile);
  const eventGroupKey = useSelector(selectTezEventGroup);
  const tezEvent = useSelector(selectTezEvent);
  const tokenData = useSelector(selectTokenData);
  const [onSelect, options, cancelLoading] = useTripOptions(
    receiptResponseIsChangeable,
    receiptResponseIsCancelable,
    hasNonSabreHotelBool,
    companySupportDetails
  );
  const { t } = useTranslation(i18nNS.tripHub);
  const { tKey } = useComponentTKey('InvoiceSummary');

  return (
    <View style={{ flex: 1, backgroundColor: 'white' }}>
      <Messages
        companyKey={EVENTS_ENABLED ? tezEvent?.CompanyKey : tokenData?.companyKey}
        groupKey={EVENTS_ENABLED ? eventGroupKey : tokenData?.groupKey}
        pageKey={pageKeys.TRIP_CONFIRMATION}
      />
      <CircleSegmentedControl
        style={{ marginBottom: -24, zIndex: 2 }}
        items={options}
        onChange={onSelect}
      />
      {cancelLoading && <LoaderOverlay loadingText={t(tKey('Cancelling'))} />}
      <InvoiceContentSummary />
    </View>
  );
};

const InvoiceContentSummary = () => {
  const { t } = useTranslation(i18nNS.tripHub);
  const { tKey } = useComponentTKey('InvoiceSummary');
  const receiptResponse = useSelector(selectFirstReceipt);
  const multiPnrResponse = useSelector(selectReceiptMultiPnr);
  const airResponse = useSelector(selectFirstTripSelectedAir);
  const currency = useSelector(selectCompanyCurrency);
  const currencyCulture = useSelector(selectCompanyCurrencyCulture);
  const availableHeight = useAvailableHeight();
  const pnrResponse = multiPnrResponse && multiPnrResponse[0];
  const amountChargedToCardToday = pnrResponse?.ItineraryTotalCostChargedToday !== 0;
  const amountChargedToCardLater = pnrResponse?.ItineraryTotalCostChargedLater !== 0;

  const modifiedReceiptResponse =
    multiPnrResponse &&
    Object.values(multiPnrResponse)
      ?.map((res) => {
        let data = {};
        const pax = res.Passengers?.map((passenger) => {
          data = {
            ...passenger,
            PNR: res?.PNR,
            KnownTravellerNumber: res?.KnownTravellerNumber,
            IsAirExists: res?.IsAirExists,
            IsCarExists: res?.IsCarExists,
            IsHotelExists: res?.IsHotelExists,
            IsRailExists: res.IsRailExists,
          };
          return data;
        });
        return pax;
      })
      .flat();
  return (
    <ScrollView style={styles.container}>
      <View style={CommonStyles.py4}>
        <BaseText style={styles.informationView}>{t(tKey('PurchasedTripInformation'))}</BaseText>
        <BaseText>
          {t(tKey('Status'))}:{' '}
          <BaseText style={styles.status}>
            {t('myTrips:MyTripsScreen.' + receiptResponse?.Status)}
          </BaseText>
        </BaseText>
      </View>
      <TitleSummaryList desc={t(tKey('TravelerInformation'))} iconName="person" />
      {multiPnrResponse?.length > 1 ? (
        <InvoiceSummaryForMultiPnr multiPnrResp={modifiedReceiptResponse} />
      ) : receiptResponse?.Passengers ? (
        receiptResponse?.Passengers.map((passenger, index) => {
          return (
            <View key={index}>
              <View style={CommonStyles.py3}>
                <BaseText style={CommonStyles.h3}>
                  {passenger?.FirstName} {passenger?.LastName}
                </BaseText>
                {passenger?.FrequentFlyers?.length > 0 && receiptResponse?.IsAirExists && (
                  <RowView>
                    <BaseText>{t(tKey('FreqFlyer'))}: </BaseText>
                    {passenger.FrequentFlyers.map((res, index) => {
                      return (
                        <BaseText key={index}>{(index ? ',' : '') + ' ' + res.ProgramNo}</BaseText>
                      );
                    })}
                  </RowView>
                )}
                {passenger?.CarLoyalty?.length > 0 && receiptResponse?.IsCarExists && (
                  <RowView>
                    <BaseText>Car Loyalty #: </BaseText>
                    {passenger.CarLoyalty.map((res, index) => {
                      return (
                        <BaseText key={index}>
                          {(index ? ',' : '') + ' ' + res.ProgramNumber}
                        </BaseText>
                      );
                    })}
                  </RowView>
                )}

                {passenger?.HotelLoyalty?.length > 0 && receiptResponse?.IsHotelExists && (
                  <RowView>
                    <BaseText>Hotel Loyalty #: </BaseText>
                    {passenger.HotelLoyalty.map((res, index) => {
                      return (
                        <BaseText key={index}>
                          {(index ? ',' : '') + ' ' + res.ProgramNumber}
                        </BaseText>
                      );
                    })}
                  </RowView>
                )}
                <RowView>
                  {receiptResponse?.KnownTravellerNumber ? (
                    <>
                      <Icon name="check" style={styles.checkIcon} />
                      <BaseText>{t(tKey('KnownTravelerNumber'))}</BaseText>
                    </>
                  ) : (
                    <></>
                  )}
                </RowView>
              </View>
              <View style={CommonStyles.py3}>
                <RowView>
                  <BaseText style={styles.informationView}>
                    {t(tKey('RecordLocator'))}: {receiptResponse?.PNR}{' '}
                  </BaseText>
                </RowView>
              </View>
            </View>
          );
        })
      ) : null}
      <View>
        <InvoiceSummaryList
          pnr={receiptResponse?.PNR}
          passengers={receiptResponse?.Passengers}
          railList={receiptResponse?.RailResponseDetails}
          airList={airResponse}
          airlistMultiPnr={multiPnrResponse}
          hotelList={receiptResponse?.HotelResponseDetails}
          carList={receiptResponse?.CarResponseDetails}
        />
      </View>
      <View style={CommonStyles.mt2}>
        <RowView style={CommonStyles.mb3}>
          <BaseText style={styles.key}>{t(tKey('TotalBaseCost'))}: </BaseText>
          {AA_ENABLED && receiptResponse?.IsRedeem ? (
            <BaseText style={styles.value}>
              receiptResponse?.AirResponseDetails?.TotalPoints
            </BaseText>
          ) : (
            <BaseText style={styles.value}>
              {formatCurrency(
                multiPnrResponse &&
                  Object.values(multiPnrResponse)
                    ?.reduce((sum, { TotalBaseCost }) => sum + TotalBaseCost, 0)
                    .toFixed(2),
                currency,
                currencyCulture,
                2
              )}
            </BaseText>
          )}
        </RowView>

        <RowView style={CommonStyles.mb3}>
          <BaseText style={styles.key}>
            {AA_ENABLED ? 'Taxes and carrier-imposed fees' : t(tKey('TaxesAndFees'))}:{' '}
          </BaseText>
          <BaseText style={styles.value}>
            {formatCurrency(
              multiPnrResponse &&
                Object.values(multiPnrResponse)
                  ?.reduce((sum, { TotalTaxCost }) => sum + TotalTaxCost, 0)
                  .toFixed(2),
              currency,
              currencyCulture,
              2
            )}
          </BaseText>
        </RowView>

        <RowView style={CommonStyles.mb3}>
          <BaseText style={styles.key}>{t(tKey('TaxRecoveryCharges'))}: </BaseText>
          <BaseText style={styles.value}>
            {formatCurrency(
              multiPnrResponse &&
                Object.values(multiPnrResponse)
                  ?.reduce((sum, { RecoveryTaxCost }) => sum + RecoveryTaxCost, 0)
                  .toFixed(2),
              currency,
              currencyCulture,
              2
            )}
          </BaseText>
        </RowView>

        <RowView style={CommonStyles.mb3}>
          <BaseText style={styles.totalKey}>{t(tKey('Total'))}: </BaseText>
          {AA_ENABLED && receiptResponse?.IsRedeem ? (
            <BaseText style={[styles.value, styles.total]}>
              {formatCurrency(
                multiPnrResponse &&
                  Object.values(multiPnrResponse)
                    ?.reduce((sum, { TotalTaxCost }) => sum + TotalTaxCost, 0)
                    .toFixed(2),
                currency,
                currencyCulture,
                2
              )}
            </BaseText>
          ) : (
            <BaseText style={[styles.value, styles.total]}>
              {formatCurrency(
                multiPnrResponse &&
                  Object.values(multiPnrResponse)
                    ?.reduce((sum, { TotalAmount }) => sum + TotalAmount, 0)
                    .toFixed(2),
                currency,
                currencyCulture,
                2
              )}
            </BaseText>
          )}
        </RowView>
        <RowView style={CommonStyles.mb3}>
          {amountChargedToCardToday ? (
            <BaseText style={styles.key}>Amount charged to card today:</BaseText>
          ) : (
            <></>
          )}
          <BaseText style={styles.value}>
            {amountChargedToCardToday ? (
              formatCurrency(
                multiPnrResponse &&
                  Object.values(multiPnrResponse)?.reduce(
                    (sum, { ItineraryTotalCostChargedToday }) =>
                      sum + ItineraryTotalCostChargedToday,
                    0
                  ),
                currency,
                currencyCulture,
                2
              )
            ) : (
              <></>
            )}
          </BaseText>
        </RowView>

        <RowView style={CommonStyles.mb5}>
          {amountChargedToCardLater ? (
            <BaseText style={styles.key}>Amount charged to card later:</BaseText>
          ) : (
            <></>
          )}
          <BaseText style={styles.value}>
            {amountChargedToCardLater ? (
              formatCurrency(
                multiPnrResponse &&
                  Object.values(multiPnrResponse)?.reduce(
                    (sum, { ItineraryTotalCostChargedLater }) =>
                      sum + ItineraryTotalCostChargedLater,
                    0
                  ),
                currency,
                currencyCulture,
                2
              )
            ) : (
              <></>
            )}
          </BaseText>
        </RowView>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    ...CommonProperties.p3,
    ...CommonProperties.pt5,
  },
  title: {
    alignSelf: 'center',
    ...CommonProperties.h2,
    ...CommonProperties.mb3,
  },
  key: {
    flex: 1,
    textAlign: 'right',
    ...CommonProperties.mr2,
    ...CommonProperties.mt2,
    ...CommonProperties.body,
  },
  value: {
    flex: 1,
    ...CommonProperties.body,
    ...CommonProperties.h2,
  },
  total: {
    color: Color.primary,
    ...CommonProperties.h2,
  },
  totalKey: {
    flex: 1,
    textAlign: 'right',
    ...CommonProperties.mt2,
    ...CommonProperties.mr2,
    ...CommonProperties.body,
  },
  informationView: {
    ...CommonProperties.h3,
  },
  status: {
    color: Color.corporate,
  },
  icon: {
    color: 'rgba(0,0,0,.3)',
    ...CommonProperties.h3,
    ...CommonProperties.mr3,
  },
  checkIcon: {
    color: Color.corporate,
    ...CommonProperties.h3,
    ...CommonProperties.mr3,
  },
  cancelIcon: {
    color: Color.required,
    ...CommonProperties.h3,
    ...CommonProperties.mr3,
  },
});

export default InvoiceSummary;
